'use client'
import { auth } from '@/lib/firebase';
import { GoogleAuthProvider, getRedirectResult, signInWithCredential, signInWithRedirect, signInWithPopup, User } from 'firebase/auth';
import React, { useCallback, useEffect } from 'react'
import { enhanceAppHypertaskUrl } from '@/lib/redirect-utils'

type GoogleCredentialResponse = {
    credential?: string;
    clientId?: string;
    select_by?: string;
};

type GooglePromptMomentNotification = {
    isNotDisplayed: () => boolean;
    getNotDisplayedReason: () => string | undefined;
    isSkippedMoment: () => boolean;
    getSkippedReason: () => string | undefined;
    isDismissedMoment: () => boolean;
    getDismissedReason: () => string | undefined;
};

type GoogleIdService = {
    initialize: (options: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        context?: 'signup' | 'signin';
    }) => void;
    prompt: (callback?: (notification: GooglePromptMomentNotification) => void) => void;
    cancel: () => void;
};

declare global {
    interface Window {
        google?: {
            accounts?: {
                id?: GoogleIdService;
            };
        };
    }
}

const GOOGLE_SIGNUP_STORAGE_KEY = 'hypertask_google_signup_completed';

export const initiateGoogleRedirectSignIn = async () => {
    if (typeof window === 'undefined') return;
    try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account',
        });
        await signInWithRedirect(auth, provider);
    } catch (error) {
        console.error('Failed to initiate Google redirect sign-in', error);
        throw error;
    }
};

const signInAndRedirect = async (idToken: string) => {

    const response = await fetch('https://app.hypertask.ai/api/auth/wordpress-signup', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            idToken,
            redirectUrl: 'https://app.hypertask.ai',
            shouldSkipInteractive: false,
            source: 'landing-page',
        }),
        credentials: 'include',
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Sign-in failed');
    }

    try {
        localStorage.setItem(GOOGLE_SIGNUP_STORAGE_KEY, 'true');
    } catch (storageError) {
        console.warn('Unable to persist Google signup flag', storageError);
    }

    if (data?.redirectUrl) {
        // Enhance redirect URL with PostHog session ID if it's an app.hypertask.ai URL
        const posthogInstance = typeof window !== 'undefined' ? (window as any).posthog : undefined
        const enhancedUrl = enhanceAppHypertaskUrl(data.redirectUrl, posthogInstance)
        window.location.href = enhancedUrl
    }
};

// Cancel GSI One Tap to prevent FedCM conflicts with Firebase popup
const cancelGSIOneTap = () => {
    try {
        const googleId = window.google?.accounts?.id;
        if (googleId && typeof googleId.cancel === 'function') {
            console.log('🟢 Canceling GSI One Tap to prevent FedCM conflict...');
            googleId.cancel();
        }
    } catch (error) {
        console.warn('Failed to cancel GSI One Tap', error);
    }
};

export const initiateGooglePopupSignIn = async () => {
    console.log('🟢 initiateGooglePopupSignIn: Function called')
    
    if (typeof window === 'undefined') {
        console.error('🔴 initiateGooglePopupSignIn: window is undefined (server-side)');
        return;
    }

    // Cancel GSI One Tap before opening Firebase popup to prevent FedCM conflicts
    cancelGSIOneTap();

    console.log('🟢 initiateGooglePopupSignIn: Checking Firebase auth...', { 
        authExists: !!auth,
        authDomain: auth?.config?.authDomain || 'missing',
    })
    
    // Check if auth is initialized
    if (!auth) {
        console.error('🔴 initiateGooglePopupSignIn: Firebase auth is not initialized');
        throw new Error('Firebase authentication is not available');
    }
    
    // Check if authDomain is configured (required for popup)
    if (!auth.config?.authDomain) {
        const errorMsg = 'Firebase authDomain is not configured. Please set NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN environment variable.';
        console.error('🔴 initiateGooglePopupSignIn:', errorMsg);
        throw new Error(errorMsg);
    }

    try {
        console.log('🟢 initiateGooglePopupSignIn: Creating GoogleAuthProvider...')
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account',
        });
        
        console.log('🟢 initiateGooglePopupSignIn: Calling signInWithPopup...', {
            authDomain: auth.config?.authDomain,
            apiKey: auth.config?.apiKey ? 'present' : 'missing',
        });
        
        const result = await signInWithPopup(auth, provider);
        console.log('🟢 initiateGooglePopupSignIn: Popup sign-in successful', result.user.email);
        
        const idToken = await result.user.getIdToken();
        console.log('🟢 initiateGooglePopupSignIn: Got ID token, redirecting...')
        await signInAndRedirect(idToken);
    } catch (error: any) {
        // Handle specific error codes
        if (error?.code === 'auth/popup-closed-by-user') {
            console.warn('Google popup closed by user');
            return;
        }
        
        if (error?.code === 'auth/popup-blocked') {
            console.error('Popup was blocked by browser. Consider using redirect flow.');
            throw new Error('Popup was blocked. Please allow popups for this site and try again.');
        }
        
        if (error?.code === 'auth/network-request-failed') {
            console.error('Network error during sign-in', error);
            throw new Error('Network error. Please check your connection and try again.');
        }
        
        if (error?.code === 'auth/unauthorized-domain') {
            console.error('Unauthorized domain for Firebase', error);
            throw new Error('Authentication error: unauthorized domain. Please contact support.');
        }
        
        // Log full error for debugging
        console.error('Failed to sign in with popup', {
            code: error?.code,
            message: error?.message,
            error: error,
        });
        
        // Re-throw with a user-friendly message
        throw new Error(error?.message || 'Failed to sign in with Google. Please try again.');
    }
};

export const GoogleSignin = () => {
    const googleClientId = "992767949049-bdr7sft7cvahqnkrkcbbkgsdsf1icmim.apps.googleusercontent.com";

    const completeSignup = useCallback(
        async (user: User) => {
            const idToken = await user.getIdToken();
            await signInAndRedirect(idToken);
        },
        []
    );

    const handleGoogleCredential = useCallback(
        async (credentialResponse: GoogleCredentialResponse) => {
            console.log("🚀 ~ HTMA639Page ~ credentialResponse:", credentialResponse)
            try {
                const idCredential = credentialResponse.credential;
                if (!idCredential) {
                    throw new Error('Google credential missing');
                }

                const firebaseCredential = GoogleAuthProvider.credential(idCredential);
                const result = await signInWithCredential(auth, firebaseCredential);

                await completeSignup(result.user);
            } catch (error) {
                console.error('Google sign-in failed', error);
            }
        },
        [completeSignup]
    );

    useEffect(() => {
        let mounted = true;

        const handleRedirect = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (mounted && result?.user) {
                    await completeSignup(result.user);
                }
            } catch (error) {
                console.error('Google redirect sign-in failed', error);
            }
        };

        handleRedirect();

        return () => {
            mounted = false;
        };
    }, [completeSignup]);

    useEffect(() => {
        if (!googleClientId) {
            console.warn('Google client ID missing');
            return;
        }


        let cancelled = false;
        let scriptLoaded = false;

        const initializeGoogle = () => {
            if (cancelled) return;

            const googleId = window.google?.accounts?.id;
            if (!googleId) {
                console.warn('Google Identity Services not available');
                return;
            }

            // Initialize Google Sign-In
            googleId.initialize({
                client_id: googleClientId,
                callback: handleGoogleCredential,
                auto_select: false,
                context: 'signup',
            });

            // Show One Tap prompt
            googleId.prompt((notification) => {
                if (notification.isNotDisplayed()) {
                    console.log('One Tap not displayed:', notification.getNotDisplayedReason());
                }
                if (notification.isSkippedMoment()) {
                    console.log('One Tap skipped:', notification.getSkippedReason());
                }
                if (notification.isDismissedMoment()) {
                    console.log('One Tap dismissed:', notification.getDismissedReason());
                }
            });

            scriptLoaded = true;
        };

        // Check if Google script is already loaded
        if (window.google?.accounts?.id && scriptLoaded === false) {
            initializeGoogle();
            return;
        }

        // Load Google Identity Services script
        if (!scriptLoaded) {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = initializeGoogle;
            script.onerror = (error) => {
                console.error('Failed to load Google Identity Services script', error);
            };
            document.head.appendChild(script);

            return () => {
                cancelled = true;
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
            };
        }
    }, [googleClientId, handleGoogleCredential]);

    return (
        <></>
    )
}
